import { useState } from 'react';
import { useNavigate, useParams, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';
import { contactQuery, useDeleteContact } from '../lib/contacts.js';
import { timelineQuery, useCompleteTask, useCreateNote } from '../lib/crm.js';
import { SuppressContactDialog } from '../components/SuppressContactDialog.js';

/** Was der Server zur Sperre mitliefert — siehe getContact() im contact-service. */
interface SperrTreffer {
  rule: { scope: string; value: string; reason?: string };
  matchedBy: string;
}

const SPERR_ART: Record<string, string> = {
  email: 'die Adresse',
  domain: 'die Domain',
  pattern: 'das Muster',
  hash: 'einen Löschvermerk',
};

const SPERR_GRUND: Record<string, string> = {
  unsubscribe: 'abgemeldet',
  bounce: 'unzustellbar',
  complaint: 'Beschwerde',
  manual: 'von Hand gesperrt',
  import: 'aus einer übernommenen Liste',
};

export function AddContactNoteForm({ contactId }: { contactId: string }) {
  const [body, setBody] = useState('');
  const createNote = useCreateNote();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    await createNote.mutateAsync({ body: body.trim(), contactId });
    setBody('');
  };

  return (
    <form onSubmit={handleSubmit} className="stack" style={{ gap: '8px' }}>
      <textarea
        className="input"
        rows={3}
        placeholder="Notiz zu diesem Kontakt erfassen (z. B. Telefonat, Vereinbarung, Feedback) …"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
      />
      <div>
        <Button type="submit" loading={createNote.isPending}>
          Notiz im Verlauf speichern
        </Button>
      </div>
    </form>
  );
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv',
  unsubscribed: 'Abgemeldet',
  bounced: 'Unzustellbar',
  complained: 'Beschwerde',
  suppressed: 'Gesperrt',
};

const CONSENT_LABELS: Record<string, string> = {
  unknown: 'Unbekannt',
  pending: 'Ausstehend',
  confirmed: 'Bestätigt',
  withdrawn: 'Widerrufen',
};

const LIFECYCLE_LABELS: Record<string, string> = {
  subscriber: 'Abonnent',
  lead: 'Lead',
  mql: 'MQL',
  sql: 'SQL',
  customer: 'Kunde',
  churned: 'Abgewandert',
};

export function ContactDetailPage() {
  // strict: false, damit die Route-ID des Elternbereichs nicht dupliziert
  // werden muss — bei code-basierten Routen ist sie sonst leicht inkonsistent.
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery(contactQuery(id));
  const remove = useDeleteContact();
  const queryClient = useQueryClient();
  const [erasing, setErasing] = useState(false);
  const [suppressing, setSuppressing] = useState(false);
  const [showSplitview, setShowSplitview] = useState(false);
  const [googleUrlInput, setGoogleUrlInput] = useState('');
  const [editingGoogleUrl, setEditingGoogleUrl] = useState(false);
  const [savingGoogleUrl, setSavingGoogleUrl] = useState(false);

  if (isLoading) return <p className="muted">Wird geladen …</p>;

  if (isError) {
    return (
      <div className="alert" data-tone="danger" role="alert">
        {error instanceof Error ? error.message : 'Kontakt konnte nicht geladen werden.'}
      </div>
    );
  }

  const contact = data?.contact;
  if (!contact) return null;

  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  const sperre = (contact as { suppression?: SperrTreffer | null }).suppression ?? null;

  const customObj = (contact.custom ?? {}) as Record<string, any>;
  const googleProfileUrl = String(
    customObj.googleProfileUrl || customObj.googleMapsUrl || customObj.googleProfile || customObj.googleMaps || '',
  );

  async function handleDelete() {
    await remove.mutateAsync(id);
    await navigate({ to: '/kontakte' });
  }

  async function handleSaveGoogleUrl(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSavingGoogleUrl(true);
      const updatedCustom = {
        ...customObj,
        googleProfileUrl: googleUrlInput.trim(),
        googleMapsUrl: googleUrlInput.trim(),
      };
      await api.patch(`/api/contacts/${id}`, { custom: updatedCustom });
      await queryClient.invalidateQueries({ queryKey: contactQuery(id).queryKey });
      setEditingGoogleUrl(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Google Profil Link konnte nicht gespeichert werden.');
    } finally {
      setSavingGoogleUrl(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/kontakte">Kontakte</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{contact.email}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>{fullName || contact.email}</h1>
          {fullName && <p className="muted">{contact.email}</p>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button
            variant={showSplitview ? 'primary' : 'secondary'}
            onClick={() => setShowSplitview(!showSplitview)}
          >
            🗺️ Splitview: Google Profil {showSplitview ? 'schließen ✕' : 'öffnen ↗'}
          </Button>

          <a className="btn" data-variant="ghost" href={`/api/contacts/${id}/export`} download>
            Daten exportieren
          </a>
          <Button variant="secondary" onClick={() => setSuppressing(true)}>
            🚫 Auf Sperrliste setzen
          </Button>
          <Button variant="secondary" onClick={handleDelete} loading={remove.isPending}>
            In den Papierkorb
          </Button>
          <Button variant="danger" onClick={() => setErasing(true)}>
            Endgültig löschen
          </Button>
        </div>
      </header>

      {suppressing && (
        <SuppressContactDialog
          contactId={id}
          email={contact.email}
          onClose={() => setSuppressing(false)}
        />
      )}

      {sperre && (
        <div className="alert" data-tone="warning" role="status">
          <strong>Wird nicht beliefert.</strong> Gesperrt über{' '}
          {SPERR_ART[sperre.rule.scope] ?? sperre.rule.scope} <code>{sperre.rule.value}</code>
          {sperre.rule.reason
            ? ` — ${SPERR_GRUND[sperre.rule.reason] ?? sperre.rule.reason}`
            : ''}. <Link to="/kontakte/sperrliste">Zur Sperrliste</Link>
        </div>
      )}

      {erasing && (
        <EraseDialog
          contactId={id}
          email={contact.email}
          onClose={() => setErasing(false)}
          onDone={() => void navigate({ to: '/kontakte' })}
        />
      )}

      {/* SPLITVIEW LAYOUT ODER STANDARD-GRID */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showSplitview ? '1fr 1fr' : '1fr',
          gap: 'var(--space-6)',
          alignItems: 'start',
        }}
      >
        {/* LINKE SPALTE (KONTAKTSTAMMDATEN + TIMELINE) */}
        <div className="stack" style={{ gap: 'var(--space-6)' }}>
          <div className="detail-grid">
            <section className="card">
              <div className="card__body stack">
                <h2 style={{ fontSize: 'var(--text-lg)' }}>Stammdaten</h2>
                <dl className="definition-grid">
                  <Entry label="Status" value={STATUS_LABELS[contact.status] ?? contact.status} />
                  <Entry
                    label="Lebenszyklus"
                    value={LIFECYCLE_LABELS[contact.lifecycleStage] ?? contact.lifecycleStage}
                  />
                  <Entry label="Telefon" value={contact.phone} />
                  <Entry label="Land" value={contact.country} />
                  <Entry label="Ort" value={contact.city} />
                  <Entry label="Quelle" value={contact.source} />
                  <Entry label="Lead-Score" value={String(contact.leadScore)} />
                  <Entry label="Angelegt" value={new Date(contact.createdAt).toLocaleString('de-CH')} />
                </dl>

                {/* GOOGLE BUSINESS PROFIL BEREICH */}
                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
                  <h3 className="field__label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>📍 Google Business Profil</span>
                    {!editingGoogleUrl && (
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        onClick={() => {
                          setGoogleUrlInput(googleProfileUrl);
                          setEditingGoogleUrl(true);
                        }}
                      >
                        {googleProfileUrl ? 'Bearbeiten' : '+ Link hinzufügen'}
                      </button>
                    )}
                  </h3>

                  {editingGoogleUrl ? (
                    <form onSubmit={handleSaveGoogleUrl} className="stack" style={{ gap: 'var(--space-2)', marginTop: '8px' }}>
                      <input
                        type="url"
                        className="input"
                        placeholder="https://maps.google.com/?cid=... oder Profil URL"
                        value={googleUrlInput}
                        onChange={(e) => setGoogleUrlInput(e.target.value)}
                        required
                        autoFocus
                      />
                      <div className="cluster" style={{ gap: '8px' }}>
                        <Button type="submit" loading={savingGoogleUrl}>
                          Speichern
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => setEditingGoogleUrl(false)}>
                          Abbrechen
                        </Button>
                      </div>
                    </form>
                  ) : googleProfileUrl ? (
                    <div className="cluster" style={{ gap: '8px', marginTop: '4px' }}>
                      <a
                        href={googleProfileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn--sm"
                        style={{ background: '#4285F4', color: '#fff' }}
                      >
                        📍 Profil öffnen ↗
                      </a>
                      <Button
                        variant="secondary"
                        style={{ fontSize: '0.85rem' }}
                        onClick={() => setShowSplitview(true)}
                      >
                        🖥️ Splitview anzeigen
                      </Button>
                    </div>
                  ) : (
                    <p className="muted text-sm" style={{ margin: 0 }}>
                      Kein Google Profil verknüpft.{' '}
                      <button
                        type="button"
                        style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => {
                          setGoogleUrlInput('');
                          setEditingGoogleUrl(true);
                        }}
                      >
                        Jetzt hinzufügen
                      </button>
                    </p>
                  )}
                </div>

                {contact.tags && contact.tags.length > 0 && (
                  <div style={{ marginTop: 'var(--space-3)' }}>
                    <h3 className="field__label">Tags</h3>
                    <div className="tag-row">
                      {contact.tags.map((tag) => (
                        <span key={tag.id} className="badge">
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="card">
              <div className="card__body stack">
                <h2 style={{ fontSize: 'var(--text-lg)' }}>Einwilligung</h2>
                <dl className="definition-grid">
                  <Entry
                    label="Status"
                    value={CONSENT_LABELS[contact.consentStatus] ?? contact.consentStatus}
                  />
                  <Entry
                    label="Erteilt am"
                    value={
                      contact.consentAt ? new Date(contact.consentAt).toLocaleString('de-CH') : null
                    }
                  />
                </dl>
                <p className="muted text-sm">
                  Der Nachweis wird beim Import oder bei der Anmeldung mitgeschrieben und ist für die
                  DSGVO-Dokumentation maßgeblich.
                </p>
              </div>
            </section>
          </div>

          <ContactTimeline contactId={id} />
        </div>

        {/* RECHTE SPALTE (SPLITVIEW GOOGLE PROFIL PROJEKTION) */}
        {showSplitview && (
          <div className="card" style={{ position: 'sticky', top: '16px', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
            <div className="card__body stack" style={{ gap: 'var(--space-4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--color-border)', paddingBottom: '12px' }}>
                <div>
                  <h2 style={{ fontSize: 'var(--text-lg)', margin: 0 }}>📍 Google Business Profil Vorschau</h2>
                  <p className="muted text-xs" style={{ margin: 0 }}>
                    {fullName || contact.email} {contact.city ? `· ${contact.city}` : ''}
                  </p>
                </div>
                <div className="cluster" style={{ gap: '8px' }}>
                  {googleProfileUrl && (
                    <a
                      href={googleProfileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn--sm btn--ghost"
                      title="In neuem Tab öffnen"
                    >
                      Im Tab öffnen ↗
                    </a>
                  )}
                  <Button variant="ghost" onClick={() => setShowSplitview(false)}>
                    ✕ Schließen
                  </Button>
                </div>
              </div>

              {googleProfileUrl ? (
                <div className="stack" style={{ gap: 'var(--space-3)' }}>
                  <div style={{ background: 'var(--color-surface-hover)', padding: '12px', borderRadius: '8px', fontSize: '0.85rem' }}>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>Verknüpfte URL:</strong>
                    <a href={googleProfileUrl} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                      {googleProfileUrl}
                    </a>
                  </div>

                  {/* EMBEDDED MAPS / PROFILE IFRAME */}
                  <div style={{ width: '100%', height: '550px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--color-border)', background: '#f8fafc' }}>
                    <iframe
                      src={
                        googleProfileUrl.includes('google.com/maps') || googleProfileUrl.includes('goo.gl/maps')
                          ? `https://maps.google.com/maps?q=${encodeURIComponent(googleProfileUrl)}&output=embed`
                          : googleProfileUrl
                      }
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      title="Google Profil Live Vorschau"
                      sandbox="allow-scripts allow-same-origin allow-popups"
                    />
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                  <p className="muted" style={{ marginBottom: '16px' }}>
                    Keine Google Profil URL für diesen Kontakt hinterlegt.
                  </p>
                  <Button
                    onClick={() => {
                      setGoogleUrlInput('');
                      setEditingGoogleUrl(true);
                    }}
                  >
                    + Google Profil Link jetzt hinterlegen
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const EVENT_LABELS: Record<string, string> = {
  sent: 'Versendet',
  delivered: 'Zugestellt',
  open: 'Geöffnet',
  click: 'Geklickt',
  reply: 'Geantwortet',
  unsubscribe: 'Abgemeldet',
  complaint: 'Beschwerde',
  bounce_hard: 'Unzustellbar',
  bounce_soft: 'Vorübergehend unzustellbar',
  failed: 'Fehlgeschlagen',
  note: 'Notiz',
  task_done: 'Aufgabe erledigt',
  stage_change: 'Stufe geändert',
  system: 'Vorgang',
};

/**
 * Eine Quelle statt drei Listen.
 *
 * E-Mail-Ereignisse, eigene Ereignisse und manuelle Vorgänge kommen bereits
 * zusammengeführt und chronologisch vom Server — die Oberfläche sortiert
 * nichts nach. Maschinelle Öffnungen sind ausgeblendet.
 */
export function ContactTimeline({ contactId }: { contactId: string }) {
  const { data, isLoading } = useQuery(timelineQuery(contactId));
  const complete = useCompleteTask();

  if (isLoading) return <p className="muted">Verlauf wird geladen …</p>;
  if (!data) return null;

  const openTasks = data.tasks.filter((task) => task.status === 'open');

  return (
    <>
      <div className="detail-grid">
        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>
              Lead-Score{' '}
              <span className="badge" data-band={data.score.band}>
                {data.score.points} · {data.score.band === 'heiss' ? 'heiß' : data.score.band}
              </span>
            </h2>
            {data.scoreHistory.length === 0 ? (
              <p className="muted text-sm">
                Noch keine Punkte vergeben. Öffnungen, Klicks und Antworten zählen automatisch,
                sobald sie eintreffen.
              </p>
            ) : (
              <>
                {/*
                  Jeder Beitrag einzeln mit Begründung. Im Altsystem war der
                  Score eine Zahl, zu der niemand sagen konnte, woher sie kam.
                */}
                <ul className="score-history">
                  {data.scoreHistory.map((entry, index) => (
                    <li key={`${entry.occurred_at}-${index}`}>
                      <span>{entry.reason}</span>
                      <span data-sign={entry.points < 0 ? 'minus' : 'plus'}>
                        {entry.points > 0 ? '+' : ''}
                        {entry.points}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="muted text-sm">Summe aller Beiträge, nach unten auf null begrenzt.</p>
              </>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Offene Aufgaben</h2>
            {openTasks.length === 0 ? (
              <p className="muted text-sm">Nichts offen.</p>
            ) : (
              <ul className="tasks">
                {openTasks.map((task) => (
                  <li key={task.id} data-overdue={task.overdue ? 'true' : undefined}>
                    <div>
                      <strong>{task.title}</strong>
                      {task.dueAt && (
                        <div className="muted text-sm">
                          fällig {new Date(task.dueAt).toLocaleString('de-CH')}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => complete.mutate(task.id)}
                      loading={complete.isPending}
                    >
                      Erledigt
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Notiz speichern</h2>
          <AddContactNoteForm contactId={contactId} />
        </div>
      </section>

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Verlauf</h2>
          {data.items.length === 0 ? (
            <p className="muted text-sm">
              Für diesen Kontakt ist noch nichts aufgezeichnet. Öffnungen, Klicks, Antworten und
              CRM-Vorgänge erscheinen hier, sobald sie eintreffen.
            </p>
          ) : (
            <ul className="timeline">
              {data.items.map((item, index) => (
                <li key={`${item.occurred_at}-${index}`} data-kind={item.kind}>
                  <strong>{EVENT_LABELS[item.type] ?? item.title}</strong>
                  {item.body && <div className="text-sm">{item.body}</div>}
                  <span className="timeline__when">
                    {new Date(item.occurred_at).toLocaleString('de-CH')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

function Entry({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value && value.length > 0 ? value : <span className="muted">—</span>}</dd>
    </div>
  );
}

/**
 * Endgueltige Loeschung (Art. 17).
 *
 * Bewusst mit Rueckfrage und getippter Bestaetigung: „Endgueltig" heisst hier
 * wirklich endgueltig — Ereignisse, Deals, Aufgaben und Notizen sind danach
 * weg, und kein Papierkorb bringt sie zurueck.
 */
function EraseDialog({
  contactId,
  email,
  onClose,
  onDone,
}: {
  contactId: string;
  email: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [typed, setTyped] = useState('');
  const [suppress, setSuppress] = useState(true);

  const erase = useMutation({
    mutationFn: () =>
      api.post<{ suppressed: boolean }>(`/api/contacts/${contactId}/erase`, { suppress }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      onDone();
    },
  });

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label="Endgültig löschen">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Endgültig löschen</h2>
          <p className="text-sm">
            Alle Daten zu diesem Kontakt werden unwiderruflich entfernt: Ereignisse, Nachrichten,
            Aufgaben, Notizen und Punktebeiträge. Das lässt sich nicht rückgängig machen.
          </p>

          {erase.isError && (
            <div className="alert" data-tone="danger" role="alert">
              {erase.error instanceof Error ? erase.error.message : 'Löschen fehlgeschlagen.'}
            </div>
          )}

          <label className="check">
            <input
              type="checkbox"
              checked={suppress}
              onChange={(e) => setSuppress(e.target.checked)}
            />
            Adresse gesperrt halten (nur als Hash)
          </label>
          {/*
            Ohne diesen Eintrag genügt ein späterer Import derselben Liste,
            und die Person bekäme wieder Post — genau das, was sie
            abbestellt hat.
          */}
          <p className="muted text-sm">
            Es bleibt kein lesbarer Eintrag zurück, nur eine Prüfsumme. Ohne sie könnte ein späterer
            Import dieselbe Adresse erneut anlegen.
          </p>

          <div className="field">
            <label className="field__label" htmlFor="erase-confirm">
              Zum Bestätigen die Adresse eingeben
            </label>
            <input
              id="erase-confirm"
              className="input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={email}
              autoComplete="off"
            />
          </div>

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => erase.mutate()}
              disabled={typed.trim().toLowerCase() !== email.toLowerCase()}
              loading={erase.isPending}
            >
              Unwiderruflich löschen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
